import DOMPurify from 'dompurify';
import { marked } from 'marked';

const HTML_TAG_PATTERN = /<\/?([a-z][a-z0-9-]*)\b[^>]*>/gi;
const KNOWN_HTML_TAGS = new Set([
  'a',
  'abbr',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);
const MARKDOWN_IMAGE_START_PATTERN = /!\[/g;
const BOLD_DATE_PATTERN = /([^\n])\s+(\*\*\d{1,2}\/\d{1,2}\/\d{2,4}(?:\s*\(\d{1,2}:\d{2}\))?\*\*)/g;
const BRACKETED_TIMESTAMP_PATTERN =
  /([^\n])\s+(\[\d{1,2}\/\d{1,2}\/\d{2,4}(?:\s+\d{1,2}:\d{2})?\])/g;
const LIST_ITEM_PATTERN = /([^\n])\s+-\s+(?=(\*\*|\[))/g;

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// Busca el cierre del texto alternativo y respeta corchetes escapados.
function findMarkdownAltEnd(text: string, startIndex: number): number {
  for (let index = startIndex; index < text.length; index += 1) {
    if (text[index] === ']' && text[index - 1] !== '\\') {
      return index;
    }
  }

  return -1;
}

// Metadata: encuentra el cierre real del destino Markdown aunque la URL tenga parentesis balanceados.
function findMarkdownImageContentEnd(text: string, startIndex: number): number {
  let nestedParenthesis = 0;
  let isInsideQuotes = false;

  for (let index = startIndex; index < text.length; index += 1) {
    const currentChar = text[index];
    const isEscaped = text[index - 1] === '\\';

    if (currentChar === '"' && !isEscaped) {
      isInsideQuotes = !isInsideQuotes;

      continue;
    }

    if (isInsideQuotes) {
      continue;
    }

    if (currentChar === '(' && !isEscaped) {
      nestedParenthesis += 1;

      continue;
    }

    if (currentChar === ')' && !isEscaped) {
      if (nestedParenthesis === 0) {
        return index;
      }

      nestedParenthesis -= 1;
    }
  }

  return -1;
}

// Separa src y title opcional sin truncar URLs que incluyen parentesis.
function parseMarkdownImageContent(content: string): { src: string; title: string } {
  const trimmedContent = content.trim();

  if (!trimmedContent) {
    return { src: '', title: '' };
  }

  if (trimmedContent.endsWith('"')) {
    for (let index = trimmedContent.length - 2; index >= 0; index -= 1) {
      if (trimmedContent[index] !== '"' || trimmedContent[index - 1] === '\\') {
        continue;
      }

      const contentBeforeTitle = trimmedContent.slice(0, index);

      if (!/\s$/.test(contentBeforeTitle)) {
        continue;
      }

      const rawSrc = contentBeforeTitle.trim();
      const src = rawSrc.replace(/^<(.+)>$/, '$1');

      return {
        src,
        title: trimmedContent.slice(index + 1, -1),
      };
    }
  }

  return {
    src: trimmedContent.replace(/^<(.+)>$/, '$1'),
    title: '',
  };
}

function convertMarkdownImages(text: string): string {
  let images = '';
  let lastIndex = 0;
  let imageStartMatch: RegExpExecArray | null;

  MARKDOWN_IMAGE_START_PATTERN.lastIndex = 0;

  while ((imageStartMatch = MARKDOWN_IMAGE_START_PATTERN.exec(text)) !== null) {
    const imageStartIndex = imageStartMatch.index;
    const altStartIndex = imageStartIndex + imageStartMatch[0].length;
    const altEndIndex = findMarkdownAltEnd(text, altStartIndex);

    if (altEndIndex === -1 || text[altEndIndex + 1] !== '(') {
      continue;
    }

    const imageContentStartIndex = altEndIndex + 2;
    const imageContentEndIndex = findMarkdownImageContentEnd(text, imageContentStartIndex);

    if (imageContentEndIndex === -1) {
      continue;
    }

    const alt = text.slice(altStartIndex, altEndIndex);
    const { src, title } = parseMarkdownImageContent(
      text.slice(imageContentStartIndex, imageContentEndIndex),
    );

    if (!src) {
      continue;
    }

    images += text.slice(lastIndex, imageStartIndex);

    const escapedSrc = escapeAttribute(src);
    const escapedAlt = escapeAttribute(alt);
    const imgTitle = title ? ` title="${escapeAttribute(title)}"` : '';

    images += `<img src="${escapedSrc}" alt="${escapedAlt}"${imgTitle} />`;
    lastIndex = imageContentEndIndex + 1;
    MARKDOWN_IMAGE_START_PATTERN.lastIndex = imageContentEndIndex + 1;
  }

  images += text.slice(lastIndex);

  return images;
}

function containsHtmlTag(text: string): boolean {
  let match: RegExpExecArray | null;

  while ((match = HTML_TAG_PATTERN.exec(text)) !== null) {
    if (KNOWN_HTML_TAGS.has(match[1].toLowerCase())) {
      HTML_TAG_PATTERN.lastIndex = 0;

      return true;
    }
  }

  HTML_TAG_PATTERN.lastIndex = 0;

  return false;
}

function normalizeMarkdownStructure(text: string): string {
  let result = text;

  // Coerce literal "\r\n" tokens and OS-specific line endings into a single newline representation
  result = result
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\r\n?/g, '\n');

  // Normalize tabs (literal or actual)
  result = result.replace(/\\t/g, '\t').replace(/\t/g, '  ');

  result = result.replace(BOLD_DATE_PATTERN, (_, before, marker) => `${before}\n\n${marker}`);
  result = result.replace(
    BRACKETED_TIMESTAMP_PATTERN,
    (_, before, marker) => `${before}\n${marker}`,
  );
  result = result.replace(LIST_ITEM_PATTERN, (_, before) => `${before}\n\n- `);
  result = result.replace(/-{4,}/g, '\n\n---\n\n');
  result = result.replace(/\n{3,}/g, '\n\n');

  return result.trim();
}

function normalizeTextNodeWhitespace(text: string): string {
  let normalized = '';

  for (let index = 0; index < text.length; index += 1) {
    const currentChar = text[index];

    if (currentChar !== '\u00A0') {
      normalized += currentChar;

      continue;
    }

    const previousChar = text[index - 1];
    const nextChar = text[index + 1];
    const hasPreviousVisibleChar = Boolean(previousChar && !/\s/.test(previousChar));
    const hasNextVisibleChar = Boolean(nextChar && !/\s/.test(nextChar));

    normalized += hasPreviousVisibleChar && hasNextVisibleChar ? ' ' : currentChar;
  }

  return normalized;
}

export function normalizeHtmlWhitespace(input?: string): string {
  if (!input) return '';

  const parser = new DOMParser();
  const document = parser.parseFromString(input, 'text/html');
  // Normaliza solo espacios no separables "internos" para no romper indentación ni espaciados intencionales.
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();

  while (currentNode) {
    const parentTagName = currentNode.parentElement?.tagName;

    if (parentTagName !== 'PRE' && parentTagName !== 'CODE') {
      currentNode.textContent = normalizeTextNodeWhitespace(currentNode.textContent ?? '');
    }

    currentNode = walker.nextNode();
  }

  return document.body.innerHTML;
}

export function ensureHtml(input?: string): string {
  if (!input) return '';

  const hasHtml = containsHtmlTag(input);
  const withImages = convertMarkdownImages(input);

  if (hasHtml) {
    return DOMPurify.sanitize(withImages);
  }

  const normalized = normalizeMarkdownStructure(withImages);
  const html = marked(normalized, { gfm: true, breaks: true, async: false });

  return DOMPurify.sanitize(html);
}
